import { create } from 'zustand';

import { LANG_CS, LANG_EN, LANG_LS_ITEM, LANGS } from "~/const";
import { TRANS as TRANS_CS } from "~/langs/cs";
import { TRANS as TRANS_EN } from "~/langs/en";
import type { DeepStringObj } from "~/interfaces";

export type TLang = typeof LANGS[number];

interface ILangStore {
	lang: TLang;
	data: DeepStringObj;
	setLang: (lang: TLang) => void;
}

const LANG_DATA: Record<TLang, DeepStringObj> = {
	[LANG_CS]: TRANS_CS,
	[LANG_EN]: TRANS_EN,
};

// novy navstevnik: cestina na domene .cz nebo s ceskym ci slovenskym prohlizecem, jinak anglictina
function getDefaultLang(): TLang {
	const browserLangs = navigator.languages?.length ? navigator.languages : [navigator.language || ""];
	const czBrowser = browserLangs.some(item => /^(cs|sk)\b/iu.test(item));

	return location.hostname.endsWith(".cz") || czBrowser ? LANG_CS : LANG_EN;
}

// <html lang>, <title> a meta popis podle jazyka
function setDocumentLang(lang: TLang) {
	const meta = LANG_DATA[lang].meta as DeepStringObj;

	document.documentElement.lang = lang;
	document.title = meta.title as string;
	document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description as string);
	document.querySelector('meta[property="og:locale"]')?.setAttribute("content", lang === LANG_EN ? "en_GB" : "cs_CZ");
}

// jazyk vybrany navstevnikem (localStorage), jinak vychozi podle domeny a prohlizece
function getInitLang(): TLang {
	try {
		const lsLang = localStorage.getItem(LANG_LS_ITEM) as TLang;

		if (LANGS.includes(lsLang)) {
			return lsLang;
		}
	} catch {
		// localStorage nemusi byt dostupny
	}

	return getDefaultLang();
}

const initLang = getInitLang();

setDocumentLang(initLang);

export const langStore = create<ILangStore>(set => ({
	lang: initLang,
	data: LANG_DATA[initLang],
	setLang: lang => {
		try {
			localStorage.setItem(LANG_LS_ITEM, lang);
		} catch {
			// localStorage nemusi byt dostupny
		}

		setDocumentLang(lang);
		set(() => ({
			lang,
			data: LANG_DATA[lang],
		}));
	},
}));

// po prihlaseni ma prednost jazyk ulozeny u uzivatele (user.lang)
export function applyUserLang(lang?: string) {
	const { lang: curLang, setLang } = langStore.getState();

	if (LANGS.includes(lang as TLang) && lang !== curLang) {
		setLang(lang as TLang);
	}
}

// locale pro Intl (datumy, nazvy dnu a mesicu)
export function getLocale(): string {
	return langStore.getState().lang === LANG_EN ? "en-GB" : "cs-CZ";
}
